import { SignOutIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";

import { API_BASE } from "../lib/api-routes.ts";

import { useWorkspace } from "./AppShell.tsx";
import { useSession } from "./session.tsx";
import { SESSION_TEXTS, SETTINGS_TEXTS } from "./texts.ts";
import {
  applyTheme,
  readTheme,
  THEME_LABELS,
  THEMES,
  type Theme,
} from "./theme.ts";

/**
 * Los ajustes, que hoy son pocos y honestos.
 *
 * Lo mismo se abre desde el chat --el engranaje de la cabecera-- y desde el
 * panel --la entrada de abajo del todo--, así que es un diálogo y no una
 * pantalla: dos pantallas de ajustes acabarían diciendo cosas distintas.
 *
 * Solo enseña lo que se sabe de verdad. Las preferencias que el asistente
 * recuerda NO están aquí: viven en Chat ▸ Memoria, que es donde se pueden leer
 * y corregir (§11).
 */
export function SettingsDialog({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [theme, setTheme] = useState<Theme>(readTheme);
  const { me, signOut } = useSession();
  const { chat } = useWorkspace();
  const [leaving, setLeaving] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{SETTINGS_TEXTS.title}</DialogTitle>
          <DialogDescription>{SETTINGS_TEXTS.memoryHint}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <span className="text-sm font-medium">{SETTINGS_TEXTS.theme}</span>
          <Tabs
            value={theme}
            onValueChange={(value) => {
              const next = value as Theme;
              setTheme(next);
              applyTheme(next);
            }}
          >
            <TabsList className="w-full">
              {THEMES.map((name) => (
                <TabsTrigger key={name} value={name} className="flex-1">
                  {THEME_LABELS[name]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <dl className="grid gap-px overflow-hidden rounded-md border text-sm sm:grid-cols-2">
          <Fact
            label={SETTINGS_TEXTS.session}
            value={me?.fullName || me?.email || SETTINGS_TEXTS.noValue}
          />
          <Fact label={SETTINGS_TEXTS.api} value={API_BASE} />
        </dl>

        <Button
          variant="outline"
          className="justify-self-start"
          disabled={leaving}
          onClick={() => {
            setLeaving(true);
            // Un turno a medias no puede seguir escribiendo en una
            // conversación de quien ya se fue.
            void signOut(chat.stop).finally(() => {
              setLeaving(false);
            });
          }}
        >
          <SignOutIcon />
          {leaving ? SESSION_TEXTS.signingOut : SESSION_TEXTS.signOut}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-secondary/40 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono" title={value}>
        {value}
      </dd>
    </div>
  );
}
